import { ScheduledTask } from "@/data/models/Task";
import User from "@/data/models/User";
import {
  createTimeSlotForSchedueldTask,
  fillTravelTimesAndPauseDurations,
  doTimeSlotsWithSchedueldTasksOverlap,
  getDateFromDayId,
  TimeSlot,
  TimeSlotWithScheduledTask,
  getTimeslotStartTimestampIncludingTravelTimeAndBreaks,
  getTimeslotEndTimestampIncludingTravelTimeAndBreaks,
  getDayId,
  getWorkingTimeForTimeSlotInMs,
} from "./TimeSlot";

import { BreakInfo } from "@/data/models/BreakInfo";

import {
  OccupiedTimeInfo,
  OccupiedTimeType,
} from "@/data/models/OccupiedTimeInfo";

import {
  TravelTimeSettings,
  PlanningControlSettings,
  OccupiedTimeUsage,
  SchedulingSettings,
  WorkingHoursSettings,
  getEarliestLunchBreakForDate,
} from "@/data/models/SchedulingSettings";
import { DateTime } from "luxon";

export class TasksOfUserLookup {
  user: User;

  private defaultTravelTimes: TravelTimeSettings;
  private planningControlSettings: PlanningControlSettings;
  private workingHoursSettings: WorkingHoursSettings;

  private usedTimeSlotsByDay = new Map<string, TimeSlotWithScheduledTask[]>();
  private timpstampsOfDayByDay = new Map<string, number>();

  private earliestLunchBreakTimes = new Map<string, DateTime>();

  private maxWorkingHoursPerDayInMs: number;
  private maxWorkingHoursPerWeekInMs: number;

  constructor(
    user: User,
    scheduledTasks: ScheduledTask[],
    defaultTravelTimes: TravelTimeSettings,
    calculationSettings: PlanningControlSettings,
    workingHoursSettings: WorkingHoursSettings
  ) {
    this.user = user;
    this.workingHoursSettings = workingHoursSettings;

    this.maxWorkingHoursPerDayInMs =
      workingHoursSettings.maxWorkingHoursPerDay * 60 * 60 * 1000;
    this.maxWorkingHoursPerWeekInMs =
      workingHoursSettings.maxWorkingHoursPerWeek * 60 * 60 * 1000;

    scheduledTasks
      .map((scheduledTask) => createTimeSlotForSchedueldTask(scheduledTask))
      .forEach((timeSlot) => {
        this.addOrUpdateTimeSlotWithSchedueldTask(timeSlot);
      });

    this.defaultTravelTimes = defaultTravelTimes;
    this.planningControlSettings = calculationSettings;
  }

  clear() {
    this.usedTimeSlotsByDay.clear();
    this.timpstampsOfDayByDay.clear();

    this.earliestLunchBreakTimes.clear();
  }

  get allTimeSlotsWithScheduledTasks() {
    const result: TimeSlotWithScheduledTask[] = [];

    for (const [, timeSlots] of this.usedTimeSlotsByDay) {
      result.push(...timeSlots);
    }

    return result;
  }

  setSchedulingSettings(schedulingSettings: SchedulingSettings) {
    this.defaultTravelTimes = schedulingSettings.travelTimeSettings;
    this.planningControlSettings = schedulingSettings.planningControlSettings;
    this.workingHoursSettings = schedulingSettings.workingHoursSettings;

    this.maxWorkingHoursPerDayInMs =
      this.workingHoursSettings.maxWorkingHoursPerDay * 60 * 60 * 1000;
    this.maxWorkingHoursPerWeekInMs =
      this.workingHoursSettings.maxWorkingHoursPerWeek * 60 * 60 * 1000;
  }

  private getTravelTimeBetweenTasksInMs() {
    return this.defaultTravelTimes.defaultTravelTimeBetweenTasksInMin * 60000;
  }

  private getLunchBreakDurationInMs() {
    return this.workingHoursSettings.lunchBreakDurationInMin * 60000;
  }

  calculateLunchBreakForDay(
    dayId: string,
    timeSlotWithScheduledTasks: TimeSlotWithScheduledTask[],
    earliestLunchBreakTime: DateTime
  ): BreakInfo | null {
    const earliestLunchBreakTimestamp = earliestLunchBreakTime.toMillis();

    const firstTimeslotAfterLunchBreakIndex = timeSlotWithScheduledTasks.findIndex(
      (timeSlot) => timeSlot.startTimestamp >= earliestLunchBreakTimestamp
    );

    if (firstTimeslotAfterLunchBreakIndex <= 0) {
      return null;
    }

    const task = timeSlotWithScheduledTasks[firstTimeslotAfterLunchBreakIndex];

    return {
      taskAfterId: task.scheduledTask.id,
      dayId: dayId,
      startTimestamp: task.startTimestamp + 10 + task.durationInMs,
      durationInMs: this.getLunchBreakDurationInMs(),
    };
  }

  doTimespansOverlap(a: TimeSlot, b: TimeSlot, ignoreTravelTime?: boolean) {
    let doOverlap = false;

    if (b.startTimestamp < a.startTimestamp) {
      const bEnd =
        b.startTimestamp +
        b.durationInMs +
        (ignoreTravelTime ? 0 : this.getTravelTimeBetweenTasksInMs());
      doOverlap = bEnd > a.startTimestamp;
    } else {
      const aEnd =
        a.startTimestamp +
        a.durationInMs +
        (ignoreTravelTime ? 0 : this.getTravelTimeBetweenTasksInMs());
      doOverlap = b.startTimestamp < aEnd;
    }

    return doOverlap;
  }

  getWorkingTimeOfDayInMs(dayId: string) {
    const usedTimeslotsOfDay = this.usedTimeSlotsByDay.get(dayId);

    if (!usedTimeslotsOfDay) {
      return 0;
    }

    let sum = 0;

    usedTimeslotsOfDay.forEach((timeSlot) => {
      sum += getWorkingTimeForTimeSlotInMs(timeSlot);
    });

    return sum;
  }

  private satisfiesWorkDayStartAndEnd(
    dayId: string,
    timeSlotsOfDay: TimeSlot[]
  ) {
    const dateTime = DateTime.fromJSDate(getDateFromDayId(dayId));

    const {
      earliestWorkDayStart,
      latestWorkDayEnd,
    } = this.workingHoursSettings;

    const startThreshold = dateTime
      .set({
        hour: earliestWorkDayStart.hour,
        minute: earliestWorkDayStart.minute,
        second: 0,
        millisecond: 0,
      })
      .valueOf();

    const endThreshold = dateTime
      .set({
        hour: latestWorkDayEnd.hour,
        minute: latestWorkDayEnd.minute,
        second: 0,
        millisecond: 0,
      })
      .valueOf();

    const planWithTravelTime =
      this.planningControlSettings.travelTimeUsage == OccupiedTimeUsage.PLAN;

    const planWithBreaks =
      this.planningControlSettings.breakUsage == OccupiedTimeUsage.PLAN;

    for (const timeSlot of timeSlotsOfDay) {
      const timeSlotStartTimestamp = getTimeslotStartTimestampIncludingTravelTimeAndBreaks(
        timeSlot,
        planWithTravelTime,
        planWithBreaks
      );

      if (timeSlotStartTimestamp < startThreshold) {
        return false;
      }

      const timeSlotEndTimestamp = getTimeslotEndTimestampIncludingTravelTimeAndBreaks(
        timeSlot,
        planWithTravelTime,
        planWithBreaks
      );

      if (timeSlotEndTimestamp > endThreshold) {
        return false;
      }
    }

    return true;
  }

  private satisfiesMaxWoringHoursConditions(timeSlotsOfDay: TimeSlot[]) {
    let workingTimeOfDayInMs = 0;

    timeSlotsOfDay.forEach((timeSlot) => {
      workingTimeOfDayInMs += getWorkingTimeForTimeSlotInMs(timeSlot);
    });

    if (workingTimeOfDayInMs > this.maxWorkingHoursPerDayInMs) {
      return false;
    }

    return true;
  }

  private getEarliestLunchBreakForDay(dayId: string) {
    return (
      this.earliestLunchBreakTimes.get(dayId) ??
      this.fillEarliestLunchBreakForDay(dayId)
    );
  }

  private fillEarliestLunchBreakForDay(dayId: string) {
    let value = this.earliestLunchBreakTimes.get(dayId);
    if (value != undefined) {
      return value;
    }

    value = getEarliestLunchBreakForDate(
      getDateFromDayId(dayId),
      this.workingHoursSettings
    );

    this.earliestLunchBreakTimes.set(dayId, value);

    return value;
  }

  private getTimestampForDay(dayId: string) {
    return (
      this.timpstampsOfDayByDay.get(dayId) ?? this.fillTimestampOfDay(dayId)
    );
  }

  private fillTimestampOfDay(dayId: string) {
    let value = this.timpstampsOfDayByDay.get(dayId);
    if (value != undefined) {
      return value;
    }

    value = getDateFromDayId(dayId).getTime();
    this.timpstampsOfDayByDay.set(dayId, value);

    return value;
  }

  fillTravelTimeAndPauseDurations() {
    for (const [, timeSlots] of this.usedTimeSlotsByDay) {
      this.fillTravelTimesAndPauseDurations(timeSlots);
    }
  }

  isTimeslotFree(timeSlot: TimeSlot, taskIdToExcludeFromOverlapCheck?: string) {
    const { dayId } = timeSlot;

    const timeSlotsForDay = this.usedTimeSlotsByDay.get(dayId) ?? [];

    const toCheck = [
      ...timeSlotsForDay.filter(
        (timeSlotWithScheduledTask) =>
          timeSlotWithScheduledTask.scheduledTask.id !=
          taskIdToExcludeFromOverlapCheck
      ),
      timeSlot,
    ].sort((a, b) => a.startTimestamp - b.startTimestamp);

    this.fillTravelTimesAndPauseDurations(toCheck);

    if (!this.satisfiesMaxWoringHoursConditions(toCheck)) {
      return null;
    }

    const satisfiesWorkDayStartAndEnd = this.satisfiesWorkDayStartAndEnd(
      dayId,
      toCheck
    );

    if (!satisfiesWorkDayStartAndEnd) {
      return null;
    }

    for (let i = 0; i < toCheck.length - 1; i++) {
      const timeSlot = toCheck[i];
      const followingTimeSlot = toCheck[i + 1];

      if (
        doTimeSlotsWithSchedueldTasksOverlap(
          timeSlot,
          followingTimeSlot,
          this.planningControlSettings
        )
      ) {
        return false;
      }
    }

    return true;
  }

  updateScheduledTask(scheduledTask: ScheduledTask) {
    const timeSlotWithSchedueldTask = createTimeSlotForSchedueldTask(
      scheduledTask
    );
    this.addOrUpdateTimeSlotWithSchedueldTask(timeSlotWithSchedueldTask);
  }

  removeTimeSlotForScheduledTask(
    scheduledTask: ScheduledTask,
    updateTravelTimeAndPauseDurations?: boolean
  ) {
    const dayId = getDayId(scheduledTask.startDate);

    const tasksForDay = this.usedTimeSlotsByDay.get(dayId);

    if (!tasksForDay) {
      return;
    }

    const remainingTimeSlots = tasksForDay
      .filter((timeSlot) => timeSlot.scheduledTask.id !== scheduledTask.id)
      .sort((a, b) => a.startTimestamp - b.startTimestamp);

    if (updateTravelTimeAndPauseDurations) {
      this.fillTravelTimesAndPauseDurations(remainingTimeSlots);
    }

    this.usedTimeSlotsByDay.set(dayId, remainingTimeSlots);
  }

  addOrUpdateTimeSlotWithSchedueldTask(
    timeSlotWithSchedueldTask: TimeSlotWithScheduledTask,
    updateTravelTimeAndPauseDurations?: boolean
  ) {
    const dayId = timeSlotWithSchedueldTask.dayId;
    const tasksForDay = this.usedTimeSlotsByDay.get(dayId);

    if (tasksForDay) {
      const currentIndex = tasksForDay.findIndex(
        (t) => t.scheduledTask.id === timeSlotWithSchedueldTask.scheduledTask.id
      );

      if (currentIndex == -1) {
        tasksForDay.push(timeSlotWithSchedueldTask);
      } else {
        tasksForDay[currentIndex] = timeSlotWithSchedueldTask;
      }

      tasksForDay.sort((a, b) => a.startTimestamp - b.startTimestamp);
    } else {
      const { dayId } = timeSlotWithSchedueldTask;

      this.usedTimeSlotsByDay.set(dayId, [timeSlotWithSchedueldTask]);

      const date = DateTime.fromJSDate(
        timeSlotWithSchedueldTask.scheduledTask.startDate
      ).set({
        hour: 12,
        minute: 0,
        second: 0,
        millisecond: 0,
      });

      this.earliestLunchBreakTimes.set(dayId, date);
    }

    if (updateTravelTimeAndPauseDurations) {
      const updatedTimeSlots =
        this.usedTimeSlotsByDay.get(timeSlotWithSchedueldTask.dayId) ?? [];

      this.fillTravelTimesAndPauseDurations(updatedTimeSlots);
    }
  }

  getOccupiedTimeInfo(startTimestamp: number, endTimestamp: number) {
    const result: OccupiedTimeInfo[] = [];

    for (const [dayId, timeSlotsOfDay] of this.usedTimeSlotsByDay) {
      const timestamp = this.getTimestampForDay(dayId);

      if (timestamp < startTimestamp || timestamp > endTimestamp) {
        continue;
      }

      timeSlotsOfDay.forEach((timeSlot, index) => {
        const {
          travelTimeToTaskInMs: travelTimeToTask,
          travelTimeFromTaskInMs: travelTimeFromTask,
          breakDurationBeforeTaskInMs: breakDurationBeforeTask,
          breakDurationAfterTaskInMs: breakDurationAfterTask,
        } = timeSlot;

        const { scheduledTask } = timeSlot;

        if (travelTimeToTask || breakDurationBeforeTask) {
          let startTimestamp = timeSlot.startTimestamp;

          if (breakDurationBeforeTask) {
            result.push({
              id: `${scheduledTask.id}_break_before_task`,
              sourceTaskId: scheduledTask.id,
              fromTaskId: `${startTimestamp}_${this.user.id}_lunch_break`,
              toTaskId: scheduledTask.id,
              userId: scheduledTask.userId,
              startTimestamp: startTimestamp - breakDurationBeforeTask,
              endTimestamp: startTimestamp,
              durationInMin: breakDurationBeforeTask / 60000,
              type: OccupiedTimeType.BREAK,
            });

            startTimestamp -= breakDurationBeforeTask;
          }

          if (travelTimeToTask) {
            result.push({
              id: `${scheduledTask.id}_travel_time_to_task`,
              sourceTaskId: scheduledTask.id,
              fromTaskId:
                timeSlotsOfDay[index - 1]?.scheduledTask?.id ?? "startOfDay",
              toTaskId: scheduledTask.id,
              userId: scheduledTask.userId,
              startTimestamp: startTimestamp - travelTimeToTask,
              endTimestamp: startTimestamp,
              durationInMin: travelTimeToTask / 60000,
              type: OccupiedTimeType.TRAVEL_TIME,
            });
          }
        }

        if (travelTimeFromTask || breakDurationAfterTask) {
          let startTimestamp = timeSlot.startTimestamp + timeSlot.durationInMs;

          if (breakDurationAfterTask) {
            result.push({
              id: `${scheduledTask.id}_break_after_task`,
              sourceTaskId: scheduledTask.id,
              fromTaskId: `${startTimestamp}_${this.user.id}_lunch_break`,
              toTaskId: scheduledTask.id,
              userId: scheduledTask.userId,
              startTimestamp: startTimestamp,
              endTimestamp: startTimestamp + breakDurationAfterTask,
              durationInMin: breakDurationAfterTask / 60000,
              type: OccupiedTimeType.BREAK,
            });

            startTimestamp += breakDurationAfterTask;
          }

          if (travelTimeFromTask) {
            result.push({
              id: `${scheduledTask.id}_travel_time_from_task`,
              sourceTaskId: scheduledTask.id,
              fromTaskId: scheduledTask.id,
              toTaskId:
                timeSlotsOfDay[index + 1]?.scheduledTask?.id ?? "endOfDay",
              userId: scheduledTask.userId,
              startTimestamp: startTimestamp,
              endTimestamp: startTimestamp + travelTimeFromTask,
              durationInMin: travelTimeFromTask / 60000,
              type: OccupiedTimeType.TRAVEL_TIME,
            });
          }
        }
      });
    }

    return result;
  }

  findFittingTimeSlot(
    timeSlot: TimeSlot,
    taskIdToExcludeFromOverlapCheck?: string
  ): TimeSlot | null {
    const { dayId } = timeSlot;

    const timeSlotsForDay = this.usedTimeSlotsByDay.get(dayId) ?? [];
    const sortedTimeSlots = [
      ...timeSlotsForDay.filter(
        (timeSlotWithScheduledTask) =>
          timeSlotWithScheduledTask.scheduledTask.id !=
          taskIdToExcludeFromOverlapCheck
      ),
    ].sort((a, b) => a.startTimestamp - b.startTimestamp);

    this.fillTravelTimesAndPauseDurations(sortedTimeSlots);

    const indexOfFirstTaskAfterTimeslot = sortedTimeSlots.findIndex(
      (t) => t.startTimestamp > timeSlot.startTimestamp
    );

    if (indexOfFirstTaskAfterTimeslot !== 0) {
      return this.isTimeslotFree(timeSlot, taskIdToExcludeFromOverlapCheck)
        ? timeSlot
        : null;
    }

    const firstTaskAfterTimeslot =
      sortedTimeSlots[indexOfFirstTaskAfterTimeslot];
    //If this is the first task of the day try to close gap between this task and the following task
    //TODO find a more efficient way to to this
    //

    const gapSizeInMs =
      getTimeslotStartTimestampIncludingTravelTimeAndBreaks(
        firstTaskAfterTimeslot,
        false,
        true
      ) -
      getTimeslotEndTimestampIncludingTravelTimeAndBreaks(
        timeSlot,
        false,
        true
      );

    //The time it takes to travel from one task back to the depot and the to the next task
    const gapSizeThresholdInMs =
      2 *
      60000 *
      this.defaultTravelTimes.defaultTravelTimeBetweenTaskAndDepotInMin;

    if (gapSizeInMs > gapSizeThresholdInMs) {
      //If the gapSize is bigger than the time it takes to travel back to
      //the depot and then travel to the next size we don't want to close the gap
      return this.isTimeslotFree(timeSlot, taskIdToExcludeFromOverlapCheck)
        ? timeSlot
        : null;
    }

    //Try without lunch break
    const timeSlotToTry: TimeSlot = {
      ...timeSlot,
      startTimestamp:
        firstTaskAfterTimeslot.startTimestamp -
        (timeSlot.durationInMs + this.getTravelTimeBetweenTasksInMs()),
    };

    if (this.isTimeslotFree(timeSlotToTry, taskIdToExcludeFromOverlapCheck)) {
      return timeSlotToTry;
    }

    //Try with lunch break
    timeSlotToTry.startTimestamp -= this.getLunchBreakDurationInMs();

    if (this.isTimeslotFree(timeSlotToTry, taskIdToExcludeFromOverlapCheck)) {
      return timeSlotToTry;
    }

    return this.isTimeslotFree(timeSlot, taskIdToExcludeFromOverlapCheck)
      ? timeSlot
      : null;
  }

  getTasksOnDay(dayId: string): TimeSlotWithScheduledTask[] {
    const timeSlots = this.usedTimeSlotsByDay.get(dayId) ?? [];
    this.fillTravelTimesAndPauseDurations(timeSlots);
    return timeSlots;
  }

  fillTravelTimesAndPauseDurations(timeSlots: TimeSlot[]) {
    if (timeSlots.length == 0) {
      return;
    }

    const dayId = timeSlots[0].dayId;

    fillTravelTimesAndPauseDurations(
      timeSlots,
      this.getEarliestLunchBreakForDay(dayId).valueOf()
    );
  }

  getTimeSlotsToTry(dayId: string, durationInMs: number) {
    const tasksOnDay = this.getTasksOnDay(dayId);

    if (tasksOnDay.length == 0) {
      return null;
    }

    const result: TimeSlot[] = [];

    tasksOnDay.forEach((timeSlot) => {
      const endOfTimeSlotTimeStamp =
        getTimeslotEndTimestampIncludingTravelTimeAndBreaks(
          timeSlot,
          false,
          false
        ) + this.getTravelTimeBetweenTasksInMs();

      const startOfTimeSlotTimestamp =
        getTimeslotStartTimestampIncludingTravelTimeAndBreaks(
          timeSlot,
          false,
          false
        ) - this.getTravelTimeBetweenTasksInMs();

      result.push({
        ...timeSlot,
        startTimestamp:
          startOfTimeSlotTimestamp -
          (durationInMs + this.getLunchBreakDurationInMs()),
        durationInMs,
      });

      result.push({
        ...timeSlot,
        startTimestamp: startOfTimeSlotTimestamp - durationInMs,
        durationInMs,
      });

      result.push({
        ...timeSlot,
        startTimestamp: endOfTimeSlotTimeStamp,
        durationInMs,
      });

      result.push({
        ...timeSlot,
        startTimestamp:
          endOfTimeSlotTimeStamp + this.getLunchBreakDurationInMs(),
        durationInMs,
      });
    });

    return result;
  }
}
